import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, RefreshCw, ChevronLeft, FileSpreadsheet, Search } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const PerformancePersonnel = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();
  
  const [typePersonnel, setTypePersonnel] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState([]);
  const [indicateurs, setIndicateurs] = useState([]);
  const [performances, setPerformances] = useState({});
  const [paieEnCours, setPaieEnCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  useEffect(() => {
    loadData();
  }, [typeId, filialeId, isConsolidated]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = personnel.filter(emp =>
        emp.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPersonnel(filtered);
    } else {
      setFilteredPersonnel(personnel);
    }
    setCurrentPage(1);
  }, [searchTerm, personnel]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [typesRes, personnelRes, indicateursRes, paieRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/personnel').catch(() => ({ data: [] })),
        http.get(`/api/indicateurs/type/${typeId}?withPond=true&filiale_id=${filialeId || 'all'}`).catch(() => ({ data: [] })),
        http.get('/api/paies/encours').catch(() => ({ data: null }))
      ]);

      setPaieEnCours(paieRes.data);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];
      const associatedIndicateurs = Array.isArray(indicateursRes.data) ? indicateursRes.data : [];

      const type = allTypes.find(t => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(type);

      let filteredPersonnel = allPersonnel.filter(p => p.type_personnel_id === parseInt(typeId));
      
      // Si contexte filiale spécifique, filtrer par filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        filteredPersonnel = filteredPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
      }
      
      setPersonnel(filteredPersonnel);
      setFilteredPersonnel(filteredPersonnel);

      // Les indicateurs sont déjà filtrés par type personnel via l'API
      setIndicateurs(associatedIndicateurs);

      // Récupérer les performances existantes depuis perf_agent
      if (filteredPersonnel.length > 0 && associatedIndicateurs.length > 0) {
        const personnelIds = filteredPersonnel.map(p => p.idpersonnel);
        const indicateurIds = associatedIndicateurs.map(i => i.idindicateur_has_type_personnel);
        
        console.log('Récupération performances pour personnel_ids:', personnelIds);
        console.log('Récupération performances pour indicateur_ids:', indicateurIds);
        
        const performancesRes = await http.get('/api/performance', {
          params: { 
            personnel_ids: personnelIds.join(','),
            indicateur_ids: indicateurIds.join(',')
          }
        });
        
        const perfMap = {};
        if (Array.isArray(performancesRes.data)) {
          performancesRes.data.forEach(perf => {
            const key = `${perf.personnel_id}_${perf.idindicateur_has_type_personnel}`;
            perfMap[key] = perf.valeur;
          });
        }
        setPerformances(perfMap);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (personnelId, indicateurId, value) => {
    const key = `${personnelId}_${indicateurId}`;
    setPerformances(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = [];
      Object.entries(performances).forEach(([key, valeur]) => {
        const [personnelId, indicateurId] = key.split('_');
        updates.push({
          personnel_id: parseInt(personnelId),
          idindicateur_has_type_personnel: parseInt(indicateurId),
          valeur: valeur || 0,
          paie_id: paieEnCours?.idpaie || null
        });
      });

      await http.post('/api/performance/bulk-update', { updates });
      alert('Performances mises à jour avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredPersonnel.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title={`Performance : ${typePersonnel?.libelle || 'Type Personnel'}`}
        subtitle="Gérer les performances des employés par indicateur"
        actions={[
          {
            label: 'Retour',
            icon: ChevronLeft,
            onClick: () => navigate('/performance'),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          },
          {
            label: 'Sauvegarder',
            icon: Save,
            onClick: handleSave,
            primary: true,
            disabled: saving
          }
        ]}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Performances</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredPersonnel.length} employé(s) - {indicateurs.length} indicateur(s)</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40 min-w-[150px]">Employé</th>
                  {indicateurs.map(ind => (
                    <th key={ind.idindicateur_has_type_personnel} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">
                      {ind.Libelle_indicateur}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentData.map(emp => (
                  <tr key={emp.idpersonnel} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm font-medium text-gray-900 w-40 min-w-[150px]">
                      {emp.nom} {emp.prenom}
                    </td>
                    {indicateurs.map(ind => {
                      const key = `${emp.idpersonnel}_${ind.idindicateur_has_type_personnel}`;
                      const value = performances[key] || '';
                      return (
                        <td key={ind.idindicateur_has_type_personnel} className="px-2 py-3 min-w-[120px]">
                          <input
                            type="number"
                            value={value}
                            onChange={(e) => handleValueChange(emp.idpersonnel, ind.idindicateur_has_type_personnel, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            placeholder="0"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage de {startIndex + 1} à {Math.min(endIndex, filteredPersonnel.length)} sur {filteredPersonnel.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                >
                  Précédent
                </button>
                <span className="px-3 py-1">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformancePersonnel;
